import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const STORAGE_KEY = 'lanura_background_effects_enabled';

interface BackgroundEffectsContextType {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
}

const BackgroundEffectsContext = createContext<BackgroundEffectsContextType | undefined>(undefined);

export const BackgroundEffectsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const setEnabled = (value: boolean) => {
    setEnabledState(value);
    localStorage.setItem(STORAGE_KEY, value.toString());
    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('backgroundEffectsChanged', { detail: { enabled: value } }),
    );
  };

  const toggle = () => setEnabled(!enabled);

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const newValue = e.newValue === 'true';
        setEnabledState(newValue);
      }
    };

    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.enabled !== undefined) {
        setEnabledState(customEvent.detail.enabled);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('backgroundEffectsChanged', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('backgroundEffectsChanged', handleCustomEvent);
    };
  }, []);

  return (
    <BackgroundEffectsContext.Provider value={{ enabled, setEnabled, toggle }}>
      {children}
    </BackgroundEffectsContext.Provider>
  );
};

export const useBackgroundEffects = () => {
  const context = useContext(BackgroundEffectsContext);
  if (context === undefined) {
    throw new Error('useBackgroundEffects must be used within a BackgroundEffectsProvider');
  }
  return context;
};
